import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { auth, getIdToken } from '../firebase';
import './Connect.css';

function Connect() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [peers, setPeers] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [loadingPeers, setLoadingPeers] = useState(false);
  const [error, setError] = useState('');

  // Pending connection requests state
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loadingPendingRequests, setLoadingPendingRequests] = useState(false);

  // Connections state
  const [connections, setConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [showConnections, setShowConnections] = useState(false);

  /* =========================
     Fetch courses (ONCE)
  ========================= */
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/courses`);
        setCourses(res.data);
      } catch (err) {
        console.error('Error fetching courses:', err);
        setError('Failed to load courses.');
      } finally {
        setLoadingCourses(false);
      }
    };
    fetchCourses();
  }, []);

  /* =========================
     Fetch pending connection requests (ONCE)
  ========================= */
  useEffect(() => {
    const fetchPendingRequests = async () => {
      const user = auth.currentUser;
      if (!user) return;

      setLoadingPendingRequests(true);
      try {
        const token = await getIdToken();
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/user/${user.uid}/connection-requests/incoming`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPendingRequests(res.data);
      } catch (err) {
        console.error('Error fetching pending requests:', err);
      } finally {
        setLoadingPendingRequests(false);
      }
    };
    fetchPendingRequests();
  }, []);

  /* =========================
     Fetch connections (ONCE or on expand)
  ========================= */
  const fetchConnections = async () => {
    const user = auth.currentUser;
    if (!user) return;

    setLoadingConnections(true);
    try {
      const token = await getIdToken();
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/user/${user.uid}/connections`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConnections(res.data); // expect array of { uid, username, email, profilePicUrl }
    } catch (err) {
      console.error('Error fetching connections:', err);
    } finally {
      setLoadingConnections(false);
    }
  };

  const toggleConnections = () => {
    if (!showConnections && connections.length === 0) {
      fetchConnections();
    }
    setShowConnections(!showConnections);
  };

  /* =========================
     Handle course selection
  ========================= */
  const handleCourseChange = async (courseId) => {
    setSelectedCourse(courseId);
    setPeers([]);
    setError('');

    if (!courseId) return;

    setLoadingPeers(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/courses/${courseId}/peers`
      );
      setPeers(res.data);
    } catch (err) {
      console.error('Error fetching peers:', err);
      setError('Failed to load users for this course.');
    } finally {
      setLoadingPeers(false);
    }
  };

  /* =========================
     Send connection request
  ========================= */
  const handleConnect = async (peer) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        alert('You must be logged in');
        return;
      }
      const token = await getIdToken();
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/user/${user.uid}/connection-requests`,
        { toUid: peer.uid, courseId: selectedCourse },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`Connection request sent to ${peer.username}`);
    } catch (err) {
      console.error('Failed to send connection request:', err);
      alert('Failed to send connection request');
    }
  };

  /* =========================
     Accept a connection request
  ========================= */
  const handleAccept = async (requestId) => {
    try {
      const token = await getIdToken();
      await axios.patch(
        `${process.env.REACT_APP_API_BASE_URL}/connection-requests/${requestId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPendingRequests(prev => prev.filter(req => req.id !== requestId));
    } catch (err) {
      console.error('Failed to accept connection request:', err);
      alert('Failed to accept request');
    }
  };

  /* =========================
     Decline a connection request
  ========================= */
  const handleDecline = async (requestId) => {
    try {
      const token = await getIdToken();
      await axios.patch(
        `${process.env.REACT_APP_API_BASE_URL}/connection-requests/${requestId}/decline`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPendingRequests(prev => prev.filter(req => req.id !== requestId));
    } catch (err) {
      console.error('Failed to decline connection request:', err);
      alert('Failed to decline request');
    }
  };

  if (loadingCourses) return <div className="connect-loading">Loading courses...</div>;

  return (
    <div className="connect-container">
      <h2 className="page-title">Connect</h2>

      {/* Pending Connection Requests */}
      <div className="pending-requests-section">
        <h3 className="section-title">Pending Connection Requests</h3>
        {loadingPendingRequests && <p className="loading-text">Loading pending requests...</p>}
        {!loadingPendingRequests && pendingRequests.length === 0 && (
          <p className="no-data-text">No pending connection requests.</p>
        )}
        {!loadingPendingRequests && pendingRequests.length > 0 && (
          <div className="pending-requests-container">
            {pendingRequests.map(request => (
              <div key={request.id} className="pending-request-card">
                <div className="request-header">
                  <img
                    src={request.from?.profilePicUrl || '/default-profile.png'}
                    alt={request.from?.username || 'User'}
                    className="requester-profile-pic"
                  />
                  <div className="requester-info">
                    <div className="requester-name">{request.from?.username}</div>
                    <div className="requester-email">{request.from?.email}</div>
                  </div>
                </div>
                <div className="request-course">{request.course?.name || 'Unknown Course'}</div>
                <div className="request-time">
                  {request.createdAt ? new Date(request.createdAt).toLocaleString() : ''}
                </div>
                <div className="request-actions">
                  <button className="accept-button" onClick={() => handleAccept(request.id)}>✓ Accept</button>
                  <button className="decline-button" onClick={() => handleDecline(request.id)}>✗ Decline</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Connections Section */}
      <div className="connections-section">
        <div className="section-header">
          <h3 className="section-title">Your Connections</h3>
          <button className="toggle-button" onClick={toggleConnections}>
            {showConnections ? '▲ Collapse' : '▼ Expand'}
          </button>
        </div>

        {showConnections && (
          <div className="connections-list">
            {loadingConnections && <p className="loading-text">Loading connections...</p>}
            {!loadingConnections && connections.length === 0 && <p className="no-data-text">No connections yet.</p>}
            {!loadingConnections && connections.length > 0 && (
              
              <ul className="connection-cards-list">
                  {connections.map(conn => (
                    <li key={conn.uid} className="connection-card">
                      <img
                        src={conn.profilePicUrl || '/default-profile.png'}
                        alt={conn.username}
                        className="connection-profile-pic"
                      />
                      <div className="connection-info">
                        <div className="connection-username">{conn.username}</div>
                        <div className="connection-email">{conn.email}</div>
                      </div>
                      {/* Chat Button */}
                      <button
                        className="chat-button"
                        onClick={() => {
                          navigate(`/dashboard/chats?user=${conn.uid}`);
                        }}
                      >
                        Chat
                      </button>
                    </li>
                  ))}
                </ul>

            )}
          </div>
        )}
      </div>

      {/* Course dropdown */}
      <div className="course-selection">
        <label htmlFor="courseSelect" className="course-label">Find Peers:</label>
        <div className="select-wrapper">
          <select
            id="courseSelect"
            value={selectedCourse}
            onChange={(e) => handleCourseChange(e.target.value)}
            className="course-select"
          >
            <option value="">-- Select a course --</option>
            {courses.map(course => (
              <option key={course.id} value={course.id}>
                {course.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Peers list */}
      {selectedCourse && (
        <div className="peers-section">
          <h3 className="section-title">Users doing this course</h3>
          {loadingPeers && <p className="loading-text">Loading users...</p>}
          {!loadingPeers && peers.length === 0 && (
            <p className="no-data-text">No users found for this course.</p>
          )}
          {!loadingPeers && peers.length > 0 && (
            <div className="peer-cards-container">
              {peers.map(peer => (
                <div key={peer.uid} className="peer-card">
                  <img
                    src={peer.profilePicUrl || '/default-profile.png'}
                    alt={peer.username}
                    className="peer-profile-pic"
                  />
                  <div className="peer-info">
                    <div className="peer-name">{peer.username}</div>
                    <div className="peer-email">{peer.email}</div>
                  </div>
                  <button
                    className="connect-button"
                    onClick={() => handleConnect(peer)}
                  >
                    Connect
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {error && <p className="error">{error}</p>}
    </div>
  );
}

export default Connect;
